import {
  ProductClass,
} from '../../graphQL/types/shared';
import {
  availableSpaceAsMultipleOfRadius,
  determineFontSizeToFit as determineFontSizeToFitCircle,
  truncateTextInCircle,
} from '../../sharedComponents/fitTextInCircle';
import {
  determineFontSizeToFit as determineFontSizeToFitRectangle,
  truncateTextInRectangle,
} from '../../sharedComponents/fitTextInRectangle';
import {
  hsColorsMap,
  measuredCharacterHeight,
  measuredCharacterWidth,
  referenceFontSize,
  sitcColorMap,
} from '../../Utils';
import {
  IMetadatum,
} from '../fetchedData/productMetadata';
import {
  getChordLength,
  INodesLinksLayoutOutput,
  ITextInRectangle,
  ITextLayoutOutput,
  IUnfilteredNode,
  labelSeparationFromNode,
  minLabelFontSize,
  polarToCartesian,
  TextType,
  UnfilteredLayoutText,
} from './Utils';

const maxCharacterHeight = measuredCharacterHeight;
const maxCharacterWidth = measuredCharacterWidth;

const PI = Math.PI;

interface INodeWithText extends IUnfilteredNode {
  text: string;
  color: string;
  section: number;
}

const getCenterNodeText = (node: INodeWithText, centerNodeRadius: number): UnfilteredLayoutText => {
  const {id, text, section} = node;
  const {fontSize, lines} = determineFontSizeToFitCircle({
    text, referenceFontSize,
    referenceMeasurement: {maxCharacterHeight, maxCharacterWidth},
    circle: {radius: centerNodeRadius},
  });
  const maxCharacterHeightAtMinFontSize = maxCharacterHeight * minLabelFontSize / referenceFontSize;
  const maxCharacterWidthAtMinFontSize = maxCharacterWidth * minLabelFontSize / referenceFontSize;

  let result: UnfilteredLayoutText;
  if (maxCharacterHeightAtMinFontSize > availableSpaceAsMultipleOfRadius * centerNodeRadius ||
    maxCharacterWidthAtMinFontSize > availableSpaceAsMultipleOfRadius * centerNodeRadius) {

    result = {
      id, section,
      type: TextType.InCircle,
      text: {showText: false},
    };
  } else {
    let adjustedLines: string[], adjustedFontSize: number;
    if (fontSize < minLabelFontSize) {
      const truncatedLines = truncateTextInCircle({
        text, referenceFontSize,
        referenceMeasurement: {maxCharacterHeight, maxCharacterWidth},
        circle: {radius: centerNodeRadius},
        fontSizeToTry: minLabelFontSize,
      });
      adjustedLines = truncatedLines.map(line => line.text);
      adjustedFontSize = minLabelFontSize;
    } else {
      adjustedLines = lines.map(line => line.text);
      adjustedFontSize = fontSize;
    }
    result = {
      id,
      section: node.section,
      type: TextType.InCircle,
      text: {
        showText: true,
        textSplitIntoLines: adjustedLines,
        fontSize: adjustedFontSize,
        centerX: 0,
        centerY: 0,
        radius: centerNodeRadius,
      },
    };
  }
  return result;
};

const getFirstLevelBoundingBoxes = (
    nodes: INodeWithText[],
    textBoxHeight: number,
    textBoxWidth: number,
    leftEdgeDistanceFromCenter: number,
  ): UnfilteredLayoutText[] => {

  const test: UnfilteredLayoutText[] = [];
  for (const {id, theta, text, color, section} of nodes) {
    const center = {r: leftEdgeDistanceFromCenter + textBoxWidth / 2, theta};
    const {x: centerX, y: centerY} = polarToCartesian(center);

    const {fontSize, lines} = determineFontSizeToFitRectangle({
      text,
      referenceFontSize,
      referenceMeasurement: {maxCharacterHeight, maxCharacterWidth},
      rectangle: {width: textBoxWidth, height: textBoxHeight},
    });

    const maxCharacterHeightAtMinFontSize = maxCharacterHeight * minLabelFontSize / referenceFontSize;
    let textLayoutResult: ITextInRectangle;
    if (textBoxHeight < maxCharacterHeightAtMinFontSize) {
      textLayoutResult = {
        text: { showText: false},
        type: TextType.InRectangle,
      };
    } else {
      let adjustedLines: Array<{text: string}>, adjustedFontSize: number;
      if (fontSize < minLabelFontSize) {
        adjustedFontSize = minLabelFontSize;

        adjustedLines = truncateTextInRectangle({
          text, referenceFontSize,
          referenceMeasurement: {maxCharacterHeight, maxCharacterWidth},
          rectangle: {width: textBoxWidth, height: textBoxHeight},
          fontSizeToTry: minLabelFontSize,
        });
      } else {
        adjustedLines = lines;
        adjustedFontSize = fontSize;
      }
      const textsOnEachLine = adjustedLines.map(line => line.text);

      textLayoutResult = {
        type: TextType.InRectangle,
        text: {
          showText: true,
          centerX, centerY,
          width: textBoxWidth,
          height: textBoxHeight,
          angle: theta / PI * 180,
          textSplitIntoLines: textsOnEachLine,
          textUnsplit: textsOnEachLine.join(' '),
          fontSize: adjustedFontSize,
          color,
        },
      };
    }

    const result: UnfilteredLayoutText = {
      ...textLayoutResult,
      id,
      type: TextType.InRectangle,
      section,
    };

    test.push(result);
  }

  return test;
};

const getTextLayout = (
    productClass: ProductClass,
    chartRadius: number,
    nodesAndLinks: INodesLinksLayoutOutput,
    metadata: Map<number, IMetadatum>,
    levelMapping: Map<number, number>,
  ): ITextLayoutOutput => {

  const {
    nodes: nodesWithoutText, links, connectionsMap,
    centerNodeRadius,
    firstLevelNodeRadius,
    secondLevelNodeRadius,
    firstLevelNodeDistanceFromCenter,
    secondLevelNodeDistanceFromCenter,
    secondLevelSmallestAngularSeparation,
    firstLevelSmallestAngularSeparation,
  } = nodesAndLinks;

  const colorMap = (productClass === ProductClass.HS) ? hsColorsMap : sitcColorMap;

  const nodesWithText: INodeWithText[] = nodesWithoutText.map(node => {
    const {id} = node;
    const {name_short_en: text} = metadata.get(id)!;
    const section = levelMapping.get(id)!;
    const color = colorMap.get(section)!;
    return {
      ...node,
      text, section, color,
    };
  });

  const secondLevelNodes = nodesWithText.filter(({depth}) => depth === 2);

  // distance of textbox's inner edge from center:
  const secondLevelDistanceTextBoxInnerEdgeFromCenter =
    secondLevelNodeDistanceFromCenter + secondLevelNodeRadius + labelSeparationFromNode;

  const secondLevelTextBoxHeight = getChordLength(
    secondLevelDistanceTextBoxInnerEdgeFromCenter, secondLevelSmallestAngularSeparation,
  );

  const secondLevelTextboxWidth =
    chartRadius - secondLevelNodeDistanceFromCenter - secondLevelNodeRadius - labelSeparationFromNode + 15;
  const secondLevelBoundingBoxes = getFirstLevelBoundingBoxes(
    secondLevelNodes, secondLevelTextBoxHeight, secondLevelTextboxWidth, secondLevelDistanceTextBoxInnerEdgeFromCenter,
  );

  const firstLevelNodes = nodesWithText.filter(({depth}) => depth === 1);

  const firstLevelDistanceTextBoxInnerEdgeFromCenter =
    firstLevelNodeDistanceFromCenter + firstLevelNodeRadius + labelSeparationFromNode;
  const firstLevelTextBoxHeight = getChordLength(
    firstLevelNodeDistanceFromCenter, firstLevelSmallestAngularSeparation,
  );
  const firstLevelTextBoxWidth =
    (secondLevelNodeDistanceFromCenter - firstLevelNodeDistanceFromCenter -
      (firstLevelNodeRadius + secondLevelNodeRadius + labelSeparationFromNode));
  const firstLevelBoundingBoxes = getFirstLevelBoundingBoxes(
    firstLevelNodes, firstLevelTextBoxHeight, firstLevelTextBoxWidth, firstLevelDistanceTextBoxInnerEdgeFromCenter,
  );

  const centerNode = nodesWithText.find(({depth}) => depth === 0)!;
  const centerNodeText = getCenterNodeText(centerNode, centerNodeRadius);

  const texts = [...secondLevelBoundingBoxes, ...firstLevelBoundingBoxes, centerNodeText];

  const nodes: IUnfilteredNode[] = nodesWithText.map(({text: _unused, ...node}) => node);

  return {
    nodes, links,
    texts,
    connectionsMap,
  };
};

export default getTextLayout;
